import { get } from "./Config";
import config from "util/config";
import { setProfile } from "features/ProfileSlice";
import { isNewApiEnabled } from "./customerProfileFetch";
import {
  computeStreakBannerStampCount,
  isCampaignWindowActive,
  normalizeStreakChallenge,
  readStoredStreak,
} from "../../utils/streakDisplay";

function extractResponseData(res) {
  return res?.data ?? res?.response?.data;
}

function isSuccessResultCode(code) {
  return String(code) === "20000";
}

function isAuthError(data) {
  const code = String(data?.resultCode ?? "");
  return code === "40100" || code === "40300";
}

function getNewApiBaseUrl() {
  const newApiUrl = config.config().NEW_API_URL;
  return newApiUrl && String(newApiUrl).trim() ? newApiUrl : undefined;
}

function pickStreakChallenge(progress) {
  if (!Array.isArray(progress) || progress.length === 0) {
    return null;
  }

  return progress.reduce((best, item) => {
    const next = Number(item?.currentStreak ?? item?.current_streak ?? 0);
    const prev = Number(best?.currentStreak ?? best?.current_streak ?? 0);

    if (next > prev) {
      return item;
    }

    if (next === prev && item?.todayQualified && !best?.todayQualified) {
      return item;
    }

    return best;
  }, progress[0]);
}

function parseStreakResponse(data) {
  if (!isSuccessResultCode(data?.resultCode)) {
    return { status: false, streakChallenge: null, progress: [], error: data };
  }

  const progress = data.resultData?.progress ?? [];
  const streakChallenge = normalizeStreakChallenge(pickStreakChallenge(progress));

  return {
    status: true,
    streakChallenge,
    progress,
    currentStreak: computeStreakBannerStampCount(streakChallenge),
    storedStreak: readStoredStreak(streakChallenge),
    error: null,
  };
}

async function fetchStreakProgressByCustomerId(customerId) {
  if (!customerId) {
    return {
      status: false,
      streakChallenge: null,
      progress: [],
      currentStreak: 0,
      error: null,
    };
  }

  try {
    const res = await get({
      url: `/api/koala/v1/customer/${customerId}/streak-progress`,
      baseURL: getNewApiBaseUrl(),
      timeOut: 20000,
    });
    return parseStreakResponse(extractResponseData(res));
  } catch (e) {
    console.log("[streak] customer streak-progress failed >>", e?.response?.data || e);
    return {
      status: false,
      streakChallenge: null,
      progress: [],
      currentStreak: 0,
      error: e?.response?.data || e,
    };
  }
}

/**
 * Public endpoint — Streak banner visibility follows kl_campaign status + from/to time.
 * @returns {{ status: boolean, active: boolean, campaigns: object[], error: any }}
 */
export async function fetchStreakCampaignActive() {
  const empty = {
    status: false,
    active: false,
    campaigns: [],
    error: null,
  };

  if (!isNewApiEnabled()) {
    return empty;
  }

  try {
    const res = await get({
      url: "/api/campaigns/streak/active",
      baseURL: getNewApiBaseUrl(),
      timeOut: 15000,
    });
    const data = extractResponseData(res);

    if (!isSuccessResultCode(data?.resultCode)) {
      return { ...empty, error: data };
    }

    const campaigns = data.resultData?.campaigns ?? [];
    const activeCampaigns = Array.isArray(campaigns)
      ? campaigns.filter((campaign) =>
          isCampaignWindowActive(
            campaign?.fromDate ?? campaign?.from_date,
            campaign?.toDate ?? campaign?.to_date,
          ),
        )
      : [];
    return {
      status: true,
      active: activeCampaigns.length > 0,
      campaigns: activeCampaigns,
      error: null,
    };
  } catch (e) {
    console.log(
      "[streak] active fetch failed >>",
      e?.response?.data || e?.message || e,
    );
    return {
      ...empty,
      error: e?.response?.data || e,
    };
  }
}

export async function fetchStreakProgress({ customerId } = {}) {
  const empty = {
    status: false,
    streakChallenge: null,
    progress: [],
    currentStreak: 0,
    error: null,
    source: null,
  };

  if (!isNewApiEnabled()) {
    return empty;
  }

  try {
    const res = await get({
      url: "/api/campaigns/streak/progress",
      baseURL: getNewApiBaseUrl(),
      timeOut: 20000,
    });
    const data = extractResponseData(res);
    const parsed = parseStreakResponse(data);

    if (parsed.status) {
      return { ...parsed, source: "auth" };
    }

    if (isAuthError(data) && customerId) {
      console.log("[streak] auth progress denied, fallback by customerId >>", data);
      const fallback = await fetchStreakProgressByCustomerId(customerId);
      return { ...fallback, source: "customer" };
    }

    console.log("[streak] progress API non-success >>", data);
    return { ...parsed, source: "auth", error: data };
  } catch (e) {
    const error = e?.response?.data || e;
    console.log("[streak] progress API failed >>", error);

    if (customerId) {
      const fallback = await fetchStreakProgressByCustomerId(customerId);
      return { ...fallback, source: "customer" };
    }

    return { ...empty, error, source: "auth" };
  }
}

export async function loadStreakProgressToStore({
  dispatch,
  existingPointView,
  customerId,
}) {
  const streakRes = await fetchStreakProgress({ customerId });

  if (!streakRes.status) {
    return { ...streakRes, applied: false };
  }

  const streakChallenge = normalizeStreakChallenge(streakRes.streakChallenge);
  const currentStreak = computeStreakBannerStampCount(streakChallenge);

  dispatch(
    setProfile({
      pointView: {
        ...(existingPointView || {}),
        streakChallenge: streakChallenge ?? {
          ...(existingPointView?.streakChallenge || {}),
          currentStreak,
          storedStreak: readStoredStreak(streakChallenge),
          todayQualified: streakRes.streakChallenge?.todayQualified,
          lastActivityDate:
            streakRes.streakChallenge?.lastActivityDate ??
            streakRes.streakChallenge?.last_activity_date ??
            null,
        },
      },
    }),
  );

  return { ...streakRes, applied: true, currentStreak, streakChallenge };
}
